import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AssetTemplateSelectorDialogComponent } from '../../../../shared/components/asset-template-selector-dialog/asset-template-selector-dialog.component';
import { AdUtils } from '../ad.utils';
import { CreativeUnitPackageService } from '../ad-unit-package.service';
import { CreativeUnitPackageQuery } from '../ad-unit-package.query';
import { Observable, tap } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
	AssetTemplateSelectCopySettings,
	AssetTemplateSelectElement,
	AssetTemplateSelectUiSettings
} from '../../../../shared/components/asset-template-selector/asset-template-selector.model';
import { GlobalQuery } from '../../global';
import { CreativeUnitPackageDto } from '../../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { AssetDto } from '../../../../../../../api/src/asset/models/asset.dto';
import { AssetTemplateSelectorDialogDataModel } from '../../../../shared/components/asset-template-selector-dialog/asset-template-selector-dialog-data.model';
import { Collection, createCollection } from '../../collection/collection.model';
import {
	getTemplateSelectorGridCollectionTemplate,
	getTemplateSelectorTableCollectionTemplate
} from './ad-package-select-dialog.data';
import {
	AddTemplateDialogDataModel
} from "../../../../shared/components/add-template-dialog/add-template-dialog.component";

@Injectable({
	providedIn: 'root'
})
export class AdPackageSelectDialogService {
	public assetTemplateElements$: Observable<AssetTemplateSelectElement[]>;

	constructor(
		private readonly dialog: MatDialog,
		private readonly creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private readonly creativeUnitPackageService: CreativeUnitPackageService,
		private readonly globalQuery: GlobalQuery
	) {
		this.assetTemplateElements$ = this.creativeUnitPackageService.selectSelectorElements();
	}

	async openDialog(canAddNew: boolean, settings: Partial<AssetTemplateSelectorDialogDataModel> = {}, setActive: boolean = true, onlyPublished: boolean = false, getLatest: boolean = false ) {
		let data: any = {
			title: 'Templates',
			elementTitle: 'Template',
			elementTitlePlural: 'Templates',
			selectType: 'single',
			viewMode: 'table',
			viewModeOptions: ['table', 'card'],
			showFileType: false,
			showAddButton: canAddNew,
			showCancelButton: false,
			showSubmitButton: false,
			elements: this.assetTemplateElements$,
			tableCollection: { ...this.createTemplateSelectorCollection(settings.showPackageMenuActions) },
			gridCollection: { ...this.createTemplateSelectorCollection(settings.showPackageMenuActions, true) },
			categories: this.globalQuery.getValue().settings.creativePackageCategories,
			...settings
		};

		if (onlyPublished) {
			data.tableCollection.queryParams = {
				sortBy: {
					id: 'modified',
					direction: 'DESC'
				},
				onlyPublished: true
			};
			data.gridCollection.queryParams = {
				sortBy: {
					id: 'modified',
					direction: 'DESC'
				},
				onlyPublished: true
			};
		}

		let dialogRef = this.dialog.open(AssetTemplateSelectorDialogComponent, {
			data: data,
			disableClose: true,
			panelClass: ['fullscreen', 'stretch', 'stretch-height']
		});

		if (canAddNew) {
			dialogRef.componentInstance.add.subscribe((data: AddTemplateDialogDataModel) => {
				dialogRef.close({ type: 'new', data});
			});
		}

		let result = await dialogRef.afterClosed().toPromise();
		console.log('result', result);

		if (result) {
			if (result.type === 'new' && !result?.data) {

			} else if (result.type === 'new' && result?.data && canAddNew) {
				console.log('adding new package');
				const pkg = AdUtils.createCreativeUnitPackage(result.data);
				return await this.creativeUnitPackageService.add(pkg).pipe(take(1),
					map(async newPkg => {
						await this.getElements().pipe(take(1)).toPromise(); // also refresh local packages
						this.setActivePackage(newPkg);
						return newPkg;
					})).toPromise();
			} else {
				return await this.creativeUnitPackageService.findOne(result?.[0], false, getLatest)
					.pipe(
						tap(creativePackage => {
							if(setActive){
								this.setActivePackage(creativePackage);
							}
						})
					).toPromise();
			}
		}
	}

	public getElements(): Observable<AssetTemplateSelectElement[]> {
		return this.creativeUnitPackageQuery.selectAll().pipe(
			map(packages =>
				packages.map(pkg => {
					return {
						id: pkg.id,
						name: pkg.name,
						tags: pkg.tags,
						// isFeatured: pkg.featured,
						// isRecent: pkg.recent,
						fileType: this.getFileType(pkg.thumbnail?.assetPath),
						categories: pkg.categories as any,
						thumbnail: pkg.thumbnail as AssetDto,
						previewUrl: pkg.thumbnail?.assetPath
					} as AssetTemplateSelectElement;
				})
			)
		);
	}

	public setActivePackage(pkg: CreativeUnitPackageDto) {
		this.creativeUnitPackageService.upsert(pkg);
		this.creativeUnitPackageService.setActive(pkg.id);
	}

	public createTemplateSelectorCollection(showPackageMenuActions: boolean = false, grid = false, showColumnsSelectButton = false): Collection {
		return createCollection({
			service: this.creativeUnitPackageService,
			query: null,
			getMethod: 'findSummaries',
			resultsPath: 'results',
			currentTemplate: grid ? getTemplateSelectorGridCollectionTemplate() : getTemplateSelectorTableCollectionTemplate(showPackageMenuActions, showColumnsSelectButton),
			appendEntities: false,
			showPaginator: true,
			paginationMode: 'paginator',
			queryParams: {
				sortBy: {
					id: 'modified',
					direction: 'DESC'
				}
			}
		});
	}

	private getFileType(previewUrl: string) {
		const extension = previewUrl?.split('.').pop();

		if (extension) {
			const image = ['jpg', 'jpeg', 'png'];
			const video = ['mp4', 'quicktime'];
			const audio = ['mpeg', 'wav'];

			if (image.includes(extension)) {
				return 'image';
			}
			if (video.includes(extension)) {
				return 'video';
			}
			if (audio.includes(extension)) {
				return 'audio';
			}
		}
		return 'other';
	}
}
